if (!customElements.get('department-request')) {
  class DepartmentRequest extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (this.form) {
        this.requestId = globalThis.crypto?.randomUUID?.() || `request-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        this.prefillFromPage();
        this.form.addEventListener('submit', (event) => this.submit(event));
        const submitButton = this.form.querySelector('[data-js-submit]');
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-disabled');
        }
      }
    }

    prefillFromPage() {
      const params = new URLSearchParams(window.location.search);
      const vehicle = params.get('vehicle');
      const purchaseInterest = params.get('purchase_interest');
      const stock = params.get('stock');
      const crmKey = params.get('crm_key');
      const productId = params.get('product_id');
      const variantId = params.get('variant_id');
      const source = params.get('source');
      if (source) this.dataset.source = source;
      const preferredDate = this.form.elements.preferred_date;
      if (preferredDate) {
        const now = new Date();
        const localToday = new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 10);
        preferredDate.min = localToday;
      }
      if (purchaseInterest && this.form.elements.purchase_interest) this.form.elements.purchase_interest.value = purchaseInterest;
      if (stock && this.form.elements.source_stock_number) this.form.elements.source_stock_number.value = stock;
      if (crmKey && this.form.elements.source_crm_match_key) this.form.elements.source_crm_match_key.value = crmKey;
      if (productId && this.form.elements.source_product_id) this.form.elements.source_product_id.value = productId;
      if (variantId && this.form.elements.source_variant_id) this.form.elements.source_variant_id.value = variantId;
      if (purchaseInterest && this.form.elements.message && !this.form.elements.message.value) {
        this.form.elements.message.value = `I’m considering ${purchaseInterest}${stock ? ` (stock #${stock})` : ''} and would like a value for my current vehicle.`;
      }
      if (!vehicle) return;
      if (this.form.elements.request_type?.tagName === 'INPUT') this.form.elements.request_type.value = vehicle;
      if (this.dataset.department !== 'Trade-In' && this.form.elements.vehicle_model) this.form.elements.vehicle_model.value = vehicle;
      if (!purchaseInterest && this.form.elements.message && !this.form.elements.message.value) {
        this.form.elements.message.value = `I’m asking about ${vehicle}${stock ? ` (stock #${stock})` : ''}.`;
      }
    }

    setStatus(message, state = '') {
      const status = this.querySelector('[data-department-status]');
      if (!status) return;
      status.textContent = message;
      status.dataset.state = state;
    }

    payload() {
      const data = Object.fromEntries(new FormData(this.form).entries());

      // Keep trade details visible with the existing receiver, which currently
      // saves the message to the lead Description. Preserve all separate fields
      // and keep the customer's trade distinct from our stock/purchase context.
      if (this.dataset.department === 'Trade-In') {
        const text = (key) => String(data[key] ?? '').trim();
        const lines = [
          'TRADE-IN APPRAISAL REQUEST',
          `Trade year: ${text('vehicle_year') || 'Not provided'}`,
          `Trade make: ${text('vehicle_make') || 'Not provided'}`,
          `Trade model: ${text('vehicle_model') || 'Not provided'}`,
          `Kilometres or hours (as entered): ${text('kilometres_or_hours') || 'Not provided'}`,
          `VIN: ${text('vin') || 'Not provided'}`,
          `Loan or lien: ${text('loan_or_lien') || 'Not provided'}`
        ];
        if (text('purchase_interest')) {
          lines.push(`Interested in purchasing: ${text('purchase_interest')}`);
        }
        if (text('source_stock_number')) {
          lines.push(`Purchase stock number (not the trade): ${text('source_stock_number')}`);
        }
        lines.push('', 'Condition, upgrades and customer comments:', data.message || 'Not provided');
        data.message = lines.join('\n');
      }

      return {
        ...data,
        crm_match_key: data.source_crm_match_key || '',
        stock_number: data.source_stock_number || '',
        product_id: data.source_product_id || '',
        variant_id: data.source_variant_id || '',
        request_id: this.requestId,
        department: this.dataset.department,
        destination_email: this.dataset.destinationEmail,
        connect_board: this.dataset.connectBoard,
        workflow: this.dataset.workflow || 'department_request',
        routing_key: this.dataset.routingKey || (this.dataset.department || '').toLowerCase().replaceAll(' ', '-'),
        module: this.dataset.module || '',
        lead_type: this.dataset.leadType || '',
        lead_source: this.dataset.leadSource || 'Website',
        source: this.dataset.source || data.source || 'shopify_department_page',
        page_url: window.location.href,
        submitted_at: new Date().toISOString()
      };
    }

    async submit(event) {
      event.preventDefault();
      const data = this.payload();
      if (data.website) return;
      const button = this.form.querySelector('[type="submit"]');
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Sending…';
      this.setStatus('');

      try {
        if (!this.dataset.endpoint) {
          const subject = encodeURIComponent(`${this.dataset.department} website request — ${data.name || 'Customer'}`);
          const body = encodeURIComponent(Object.entries(data).filter(([key, value]) => value && !['website','destination_email'].includes(key)).map(([key, value]) => `${key.replaceAll('_',' ')}: ${value}`).join('\n'));
          window.location.href = `mailto:${this.dataset.destinationEmail}?subject=${subject}&body=${body}`;
          this.setStatus('Your email app has opened. Please send the prepared message to our team.', 'success');
          return;
        }

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(data),
          signal: controller.signal
        });
        clearTimeout(timeout);
        if (!response.ok) throw new Error('Request failed');
        this.form.reset();
        this.requestId = globalThis.crypto?.randomUUID?.() || `request-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        this.setStatus(this.dataset.successMessage || 'Thanks — your request has been sent to our team.', 'success');
      } catch (error) {
        console.error(error);
        this.setStatus(`We could not send the form. Please email ${this.dataset.destinationEmail}.`, 'error');
      } finally {
        button.disabled = false;
        button.textContent = original;
      }
    }
  }
  customElements.define('department-request', DepartmentRequest);
}
